import { ChevronDown, ChevronRight, FileUp, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BILLING_METHODS,
  COMPANY_STATUSES,
  COMPANY_TYPES,
  CONTACT_ROLES,
  DELIVERY_CHANNELS,
  ITEM_KINDS,
  ITEM_STATUSES,
  MAX_DISCOUNT_PERCENT,
  PARTY_ROLES,
  TAX_SCENARIOS,
  checkTaxId,
  type BillingMethod,
  type CompanyStatus,
  type CompanyType,
  type DeliveryChannel,
  type ItemKind,
  type ItemStatus,
  type PartyRole,
  type PaymentAdjustment,
  type PaymentBreakdown,
  type PaymentTerms,
  type TaxScenario,
} from '@pz/core';
import type { ProcOutput } from '@pz/server';
import { readTextFile, saveFile } from '../lib/download';
import { lang, money, num as fmtNum } from '../lib/format';
import { useM, useQ } from '../lib/hooks';
import { useMe } from '../lib/me';
import { Button, Checkbox, ConfirmModal, ErrorBox, Field, Input, Loading, Modal, Notice, Pill, Select, Textarea, td, th, useToast, type Tone } from './ui';

export type CompanyRow = ProcOutput<'customers.companies'>['companies'][number];
export type ContactRow = ProcOutput<'customers.contacts'>['contacts'][number];
/** What the contact dialog needs: the contact itself (the list adds the data of the company). */
export type ContactBase = Omit<ContactRow, 'companyName' | 'street' | 'postalCode' | 'city' | 'country' | 'status' | 'companyNote'>;

/* ------------------------------------------------------------------ */
/* Status of a customer relationship (the six statuses of the team)     */
/* ------------------------------------------------------------------ */

export const STATUS_TONE: Record<CompanyStatus, Tone> = {
  prospect: 'info',
  offered: 'accent',
  active: 'good',
  former: 'neutral',
  partner: 'accent',
  do_not_contact: 'bad',
};

export function CompanyStatusPill({ status }: { status: CompanyStatus }) {
  const { t } = useTranslation();
  return <Pill tone={STATUS_TONE[status]}>{t(`customers.statuses.${status}`)}</Pill>;
}

const num = (v: string): number | null => {
  const x = Number(v.replace(',', '.'));
  return v.trim() === '' || Number.isNaN(x) ? null : x;
};
const cents = (v: string): number => Math.round((num(v) ?? 0) * 100);
const eur = (c: number) => (c / 100).toFixed(2).replace('.', lang() === 'en' ? '.' : ',');

/* ------------------------------------------------------------------ */
/* Payment terms: fields and example calculation (project terms and invoice document) */
/* ------------------------------------------------------------------ */

export interface PaymentForm {
  paymentDays: string;
  discountPercent: string;
  discountDays: string;
  adjustments: { label: string; mode: 'percent' | 'amount'; value: string }[];
}

export const EMPTY_PAYMENT: PaymentForm = { paymentDays: '', discountPercent: '', discountDays: '', adjustments: [] };

/** Form values of payment terms (a fixed deduction is shown in euros, stored in cents). */
export function paymentFormOf(terms: PaymentTerms | null | undefined): PaymentForm {
  if (!terms) return EMPTY_PAYMENT;
  return {
    paymentDays: terms.paymentDays === null || terms.paymentDays === undefined ? '' : String(terms.paymentDays),
    discountPercent: terms.discountPercent ? String(terms.discountPercent) : '',
    discountDays: terms.discountDays ? String(terms.discountDays) : '',
    adjustments: (terms.adjustments ?? []).map((a) => ({ label: a.label, mode: a.mode, value: a.mode === 'amount' ? eur(a.value) : String(a.value) })),
  };
}

/** The terms as the server takes them: whole days, deductions without empty lines, fixed amounts in cents. */
export function paymentTermsOf(f: PaymentForm): PaymentTerms & { adjustments: PaymentAdjustment[] } {
  const days = num(f.paymentDays);
  const pct = num(f.discountPercent);
  const dd = num(f.discountDays);
  return {
    paymentDays: days === null ? null : Math.round(days),
    discountPercent: pct,
    discountDays: dd === null ? null : Math.round(dd),
    adjustments: f.adjustments
      .filter((a) => a.label.trim() && (num(a.value) ?? 0) > 0)
      .map((a) => ({ label: a.label.trim(), mode: a.mode, value: a.mode === 'amount' ? cents(a.value) : (num(a.value) ?? 0) })),
  };
}

/** Payment term, discount for early payment and deductions after VAT: the same fields for a project's terms and for one invoice document. */
export function PaymentFields({ value, onChange, disabled, daysHint }: { value: PaymentForm; onChange: (patch: Partial<PaymentForm>) => void; disabled?: boolean; daysHint?: ReactNode }) {
  const { t } = useTranslation();
  const setAdj = (i: number, patch: Partial<PaymentForm['adjustments'][number]>) => onChange({ adjustments: value.adjustments.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  return (
    <>
      <Field label={t('customers.terms.paymentDays')} hint={daysHint}>
        <Input type="number" min={0} max={365} inputMode="numeric" disabled={disabled} value={value.paymentDays} onChange={(e) => onChange({ paymentDays: e.target.value })} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field
          label={t('customers.terms.discountPercent')}
          error={(num(value.discountPercent) ?? 0) > MAX_DISCOUNT_PERCENT ? t('customers.terms.discountMax', { max: MAX_DISCOUNT_PERCENT }) : undefined}
        >
          <Input inputMode="decimal" placeholder="1,5" disabled={disabled} value={value.discountPercent} onChange={(e) => onChange({ discountPercent: e.target.value })} />
        </Field>
        <Field label={t('customers.terms.discountDays')}>
          <Input type="number" min={0} max={365} inputMode="numeric" placeholder="7" disabled={disabled} value={value.discountDays} onChange={(e) => onChange({ discountDays: e.target.value })} />
        </Field>
      </div>
      <div className="md:col-span-2">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[13px] font-medium text-graphite">{t('customers.terms.adjustments')}</span>
          <Button size="sm" icon={<Plus className="size-3.5" />} disabled={disabled || value.adjustments.length >= 6} onClick={() => onChange({ adjustments: [...value.adjustments, { label: '', mode: 'percent', value: '' }] })}>
            {t('customers.terms.addAdjustment')}
          </Button>
        </div>
        <p className="mb-2 text-xs text-graphite">{t('customers.terms.adjustmentsHint')}</p>
        {value.adjustments.map((a, i) => (
          <div key={i} className="mb-2 grid grid-cols-[1fr_12rem_7rem_auto] items-center gap-2">
            <Input value={a.label} maxLength={80} disabled={disabled} placeholder={t('customers.terms.adjustmentLabel')} onChange={(e) => setAdj(i, { label: e.target.value })} />
            <Select value={a.mode} disabled={disabled} onChange={(e) => setAdj(i, { mode: e.target.value as 'percent' | 'amount' })}>
              <option value="percent">{t('customers.terms.modePercent')}</option>
              <option value="amount">{t('customers.terms.modeAmount')}</option>
            </Select>
            <Input inputMode="decimal" disabled={disabled} value={a.value} onChange={(e) => setAdj(i, { value: e.target.value })} />
            <Button variant="ghost" size="sm" disabled={disabled} aria-label={t('common.delete')} onClick={() => onChange({ adjustments: value.adjustments.filter((_, j) => j !== i) })}>
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}
      </div>
    </>
  );
}

/** What the customer pays: net, VAT, gross, deductions after VAT, amount to pay at the due date, amount with discount. */
export function PaymentBreakdownTable({ breakdown, vatPercent, days, text }: { breakdown: PaymentBreakdown; vatPercent: number; days: number | null; text?: string }) {
  const { t } = useTranslation();
  return (
    <>
      <table className="num w-full text-sm">
        <tbody>
          <tr>
            <td>{t('customers.terms.net')}</td>
            <td className="text-right">{money(breakdown.netCents)}</td>
          </tr>
          <tr>
            <td>{t('customers.terms.vat', { percent: vatPercent })}</td>
            <td className="text-right">{money(breakdown.vatCents)}</td>
          </tr>
          <tr className="border-t border-line font-medium">
            <td>{t('customers.terms.gross')}</td>
            <td className="text-right">{money(breakdown.grossCents)}</td>
          </tr>
          {breakdown.adjustments.map((a, i) => (
            <tr key={i} className="text-bad">
              <td>{a.label}</td>
              <td className="text-right">{money(a.amountCents)}</td>
            </tr>
          ))}
          <tr className="border-t border-line font-semibold">
            <td>{days !== null ? t('customers.terms.payableDue', { days }) : t('customers.terms.payable')}</td>
            <td className="text-right">{money(breakdown.payableCents)}</td>
          </tr>
          {breakdown.discount && (
            <tr className="text-good">
              <td>{t('customers.terms.payableDiscount', { percent: fmtNum(breakdown.discount.percent), days: breakdown.discount.days })}</td>
              <td className="text-right font-semibold">{money(breakdown.discount.payableCents)}</td>
            </tr>
          )}
        </tbody>
      </table>
      {text && (
        <p className="mt-2 text-xs text-graphite">
          {t('customers.terms.documentText')}: <span className="text-ink">{text}</span>
        </p>
      )}
      <p className="mt-2 text-xs text-graphite">{t('customers.terms.taxAdvisorNote')}</p>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Company dialog                                                       */
/* ------------------------------------------------------------------ */

interface CompanyForm {
  id?: string;
  name: string;
  legalName: string;
  type: CompanyType;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  vatId: string;
  leitwegId: string;
  customerNo: string;
  debitorNo: string;
  status: CompanyStatus;
  ownerId: string;
  note: string;
}

function companyForm(c?: Partial<CompanyRow>): CompanyForm {
  return {
    id: c?.id,
    name: c?.name ?? '',
    legalName: c?.legalName ?? '',
    type: c?.type ?? 'company',
    street: c?.street ?? '',
    postalCode: c?.postalCode ?? '',
    city: c?.city ?? '',
    country: c?.country ?? 'DE',
    vatId: c?.vatId ?? '',
    leitwegId: c?.leitwegId ?? '',
    customerNo: c?.customerNo ?? '',
    debitorNo: c?.debitorNo ?? '',
    status: c?.status ?? 'prospect',
    ownerId: c?.ownerId ?? '',
    note: c?.note ?? '',
  };
}

/** Name, address, status and note are enough for the contact list; the data the invoice needs are in a second section. */
export function CompanyDialog({ open, company, owners, onClose, onSaved }: { open: boolean; company?: Partial<CompanyRow> | null; owners: { id: string; name: string }[]; onClose: () => void; onSaved?: (id: string) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [f, setF] = useState<CompanyForm>(() => companyForm(company ?? undefined));
  const [more, setMore] = useState(false);
  useEffect(() => {
    if (open) {
      setF(companyForm(company ?? undefined));
      setMore(!!(company && (company.vatId || company.leitwegId || company.customerNo || company.debitorNo || company.legalName || company.type !== 'company')));
    }
  }, [open, company]);
  const save = useM('customers.saveCompany', { invalidate: ['customers.'] });
  const set = <K extends keyof CompanyForm>(k: K, v: CompanyForm[K]) => setF((x) => ({ ...x, [k]: v }));
  const tax = checkTaxId(f.vatId, f.country || 'DE');
  const submit = () =>
    save.mutate(
      {
        id: f.id,
        name: f.name.trim(),
        legalName: f.legalName.trim() || null,
        type: f.type,
        street: f.street.trim() || null,
        postalCode: f.postalCode.trim() || null,
        city: f.city.trim() || null,
        country: (f.country || 'DE').trim().toUpperCase(),
        vatId: f.vatId.trim() || null,
        leitwegId: f.leitwegId.trim() || null,
        customerNo: f.customerNo.trim() || null,
        debitorNo: f.debitorNo.trim() || null,
        status: f.status,
        ownerId: f.ownerId || null,
        note: f.note.trim() || null,
      },
      {
        onSuccess: (r) => {
          toast.ok(t('common.saved'));
          onSaved?.(r.id);
          onClose();
        },
      },
    );
  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={f.id ? t('customers.company.editTitle') : t('customers.company.newTitle')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} disabled={!f.name.trim() || f.country.trim().length !== 2} onClick={submit}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 md:grid-cols-2">
        <Field label={t('customers.company.name')} className="md:col-span-2">
          <Input value={f.name} maxLength={200} autoFocus onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label={t('customers.company.status')}>
          <Select value={f.status} onChange={(e) => set('status', e.target.value as CompanyStatus)}>
            {COMPANY_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`customers.statuses.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('customers.company.owner')} hint={t('customers.company.ownerHint')}>
          <Select value={f.ownerId} onChange={(e) => set('ownerId', e.target.value)}>
            <option value="">{t('customers.company.noOwner')}</option>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('customers.company.street')} className="md:col-span-2">
          <Input value={f.street} onChange={(e) => set('street', e.target.value)} />
        </Field>
        <div className="grid grid-cols-[7rem_1fr_5rem] gap-3 md:col-span-2">
          <Field label={t('customers.company.postalCode')}>
            <Input value={f.postalCode} onChange={(e) => set('postalCode', e.target.value)} />
          </Field>
          <Field label={t('customers.company.city')}>
            <Input value={f.city} onChange={(e) => set('city', e.target.value)} />
          </Field>
          <Field label={t('customers.company.country')} hint={t('customers.company.countryHint')}>
            <Input value={f.country} maxLength={2} className="uppercase" onChange={(e) => set('country', e.target.value.toUpperCase())} />
          </Field>
        </div>
        <Field label={t('customers.company.note')} className="md:col-span-2">
          <Textarea rows={3} value={f.note} maxLength={2000} onChange={(e) => set('note', e.target.value)} />
        </Field>

        <div className="md:col-span-2">
          <button type="button" className="flex items-center gap-1 text-sm font-medium text-accent hover:underline" onClick={() => setMore(!more)} aria-expanded={more}>
            {more ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />} {t('customers.company.more')}
          </button>
          <p className="mt-0.5 text-xs text-graphite">{t('customers.company.moreHint')}</p>
        </div>
        {more && (
          <>
            <Field label={t('customers.company.type')}>
              <Select value={f.type} onChange={(e) => set('type', e.target.value as CompanyType)}>
                {COMPANY_TYPES.map((x) => (
                  <option key={x} value={x}>
                    {t(`customers.types.${x}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('customers.company.legalName')}>
              <Input value={f.legalName} onChange={(e) => set('legalName', e.target.value)} />
            </Field>
            <Field label={t('customers.company.vatId')} hint={tax.status === 'empty' ? t('customers.company.vatIdHint') : tax.status === 'ok' ? t('customers.company.taxOk') : undefined} error={tax.status === 'bad' ? t('customers.company.taxBad') : undefined}>
              <Input value={f.vatId} onChange={(e) => set('vatId', e.target.value)} />
            </Field>
            {f.type === 'public' && (
              <Field label={t('customers.company.leitwegId')} hint={t('customers.company.leitwegHint')}>
                <Input value={f.leitwegId} onChange={(e) => set('leitwegId', e.target.value)} />
              </Field>
            )}
            <Field label={t('customers.company.customerNo')}>
              <Input value={f.customerNo} onChange={(e) => set('customerNo', e.target.value)} />
            </Field>
            <Field label={t('customers.company.debitorNo')} hint={t('customers.company.debitorHint')}>
              <Input value={f.debitorNo} onChange={(e) => set('debitorNo', e.target.value)} />
            </Field>
          </>
        )}
      </div>
      <div className="mt-3">
        <ErrorBox error={save.error} />
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Contact dialog                                                       */
/* ------------------------------------------------------------------ */

interface ContactForm {
  id?: string;
  companyId: string;
  firstName: string;
  lastName: string;
  position: string;
  email: string;
  phone: string;
  roles: string[];
  note: string;
  active: boolean;
}

export function ContactDialog({
  open,
  contact,
  defaultCompanyId,
  onClose,
}: {
  open: boolean;
  contact?: ContactBase | null;
  defaultCompanyId?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const companies = useQ('customers.companies', {}, { enabled: open });
  const [f, setF] = useState<ContactForm>({ companyId: '', firstName: '', lastName: '', position: '', email: '', phone: '', roles: [], note: '', active: true });
  const [newCompany, setNewCompany] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF({
      id: contact?.id,
      companyId: contact?.companyId ?? defaultCompanyId ?? '',
      firstName: contact?.firstName ?? '',
      lastName: contact?.lastName ?? '',
      position: contact?.position ?? '',
      email: contact?.email ?? '',
      phone: contact?.phone ?? '',
      roles: contact?.roles ?? [],
      note: contact?.note ?? '',
      active: contact?.active ?? true,
    });
  }, [open, contact, defaultCompanyId]);
  const save = useM('customers.saveContact', { invalidate: ['customers.'] });
  const del = useM('customers.deleteContact', { invalidate: ['customers.'] });
  const set = <K extends keyof ContactForm>(k: K, v: ContactForm[K]) => setF((x) => ({ ...x, [k]: v }));
  const company = companies.data?.companies.find((c) => c.id === f.companyId);
  const submit = () =>
    save.mutate(
      {
        id: f.id,
        companyId: f.companyId,
        firstName: f.firstName.trim() || null,
        lastName: f.lastName.trim(),
        position: f.position.trim() || null,
        email: f.email.trim() || null,
        phone: f.phone.trim() || null,
        roles: f.roles as never,
        note: f.note.trim() || null,
        active: f.active,
      },
      {
        onSuccess: () => {
          toast.ok(t('common.saved'));
          onClose();
        },
      },
    );
  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        wide
        title={f.id ? t('customers.contact.editTitle') : t('customers.contact.newTitle')}
        footer={
          <>
            {f.id && (
              <Button variant="danger" className="mr-auto" icon={<Trash2 className="size-4" />} onClick={() => setConfirmDelete(true)}>
                {t('common.delete')}
              </Button>
            )}
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button variant="primary" loading={save.isPending} disabled={!f.companyId || !f.lastName.trim()} onClick={submit}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="grid gap-3 md:grid-cols-2">
          <div className="flex items-end gap-2 md:col-span-2">
            <Field label={t('customers.contact.company')} className="min-w-0 flex-1">
              <Select
                value={f.companyId}
                placeholder={t('customers.contact.pickCompany')}
                options={(companies.data?.companies ?? []).map((c) => ({ value: c.id, label: c.city ? `${c.name} (${c.city})` : c.name }))}
                onChange={(e) => set('companyId', e.target.value)}
              />
            </Field>
            <Button icon={<Plus className="size-4" />} onClick={() => setNewCompany(true)}>
              {t('customers.contact.newCompany')}
            </Button>
          </div>
          {company && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-graphite md:col-span-2">
              <CompanyStatusPill status={company.status} />
              <span>{[company.street, [company.postalCode, company.city].filter(Boolean).join(' '), company.country].filter(Boolean).join(', ')}</span>
              {company.status === 'do_not_contact' && <span className="font-medium text-bad">{t('customers.dncHint')}</span>}
            </div>
          )}
          <Field label={t('customers.contact.firstName')}>
            <Input value={f.firstName} onChange={(e) => set('firstName', e.target.value)} />
          </Field>
          <Field label={t('customers.contact.lastName')}>
            <Input value={f.lastName} onChange={(e) => set('lastName', e.target.value)} />
          </Field>
          <Field label={t('customers.contact.position')}>
            <Input value={f.position} onChange={(e) => set('position', e.target.value)} />
          </Field>
          <Field label={t('customers.contact.phone')}>
            <Input type="tel" value={f.phone} onChange={(e) => set('phone', e.target.value)} />
          </Field>
          <Field label={t('customers.contact.email')} className="md:col-span-2">
            <Input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} />
          </Field>
          <Field label={t('customers.contact.note')} className="md:col-span-2">
            <Textarea rows={3} value={f.note} maxLength={1000} onChange={(e) => set('note', e.target.value)} />
          </Field>
          <fieldset className="md:col-span-2">
            <legend className="mb-1 text-[13px] font-medium text-graphite">{t('customers.contact.responsible')}</legend>
            <div className="flex flex-wrap gap-x-5 gap-y-1">
              {CONTACT_ROLES.map((r) => (
                <Checkbox key={r} label={t(`customers.contactRoles.${r}`)} checked={f.roles.includes(r)} onChange={() => set('roles', f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r])} />
              ))}
            </div>
          </fieldset>
          {f.id && <Checkbox className="md:col-span-2" label={t('customers.contact.active')} hint={t('customers.contact.activeHint')} checked={f.active} onChange={(e) => set('active', e.target.checked)} />}
        </div>
        <div className="mt-3">
          <ErrorBox error={save.error ?? del.error} />
        </div>
      </Modal>
      <CompanyDialog open={newCompany} owners={companies.data?.owners ?? []} onClose={() => setNewCompany(false)} onSaved={(id) => set('companyId', id)} />
      <ConfirmModal
        open={confirmDelete}
        danger
        title={t('customers.contact.deleteTitle')}
        text={t('customers.contact.deleteText')}
        confirmLabel={t('common.delete')}
        loading={del.isPending}
        error={del.error}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() =>
          f.id &&
          del.mutate(
            { id: f.id },
            {
              onSuccess: () => {
                setConfirmDelete(false);
                toast.ok(t('common.deleted'));
                onClose();
              },
            },
          )
        }
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* CSV import of the contact list                                       */
/* ------------------------------------------------------------------ */

const ACTION_TONE = { create_company: 'good', add_contact: 'good', skip_duplicate: 'neutral', error: 'bad' } as const;

export function ImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const run = useM('customers.importContacts', { invalidate: ['customers.'] });
  useEffect(() => {
    if (open) {
      setCsv('');
      setFileName('');
      run.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const preview = run.data && !run.data.applied ? run.data : null;
  const done = run.data?.applied ? run.data : null;

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);
    const text = await readTextFile(file);
    setCsv(text);
    run.mutate({ csv: text, apply: false });
  };
  const sum = (preview ?? done)?.summary;
  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={t('customers.import.title')}
      footer={
        done ? (
          <Button variant="primary" onClick={onClose}>
            {t('common.close')}
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!preview || !(sum && sum.newContacts + sum.newCompanies > 0)}
              loading={run.isPending && run.variables?.apply}
              onClick={() => run.mutate({ csv, apply: true }, { onSuccess: (r) => r.applied && toast.ok(t('customers.import.done', { contacts: r.summary.newContacts, companies: r.summary.newCompanies })) })}
            >
              {t('customers.import.apply')}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-graphite">{t('customers.import.intro')}</p>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={input} type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={(e) => void pick(e.target.files?.[0])} />
          <Button icon={<FileUp className="size-4" />} onClick={() => input.current?.click()}>
            {t('customers.import.choose')}
          </Button>
          {fileName && <span className="text-sm text-graphite">{fileName}</span>}
        </div>
        <details className="text-xs text-graphite">
          <summary className="cursor-pointer">{t('customers.import.formatTitle')}</summary>
          <p className="mt-1">{t('customers.import.format')}</p>
        </details>
        {run.isPending && !run.variables?.apply && <Loading />}
        <ErrorBox error={run.error} />
        {sum && (
          <>
            <div className="flex flex-wrap gap-2 text-sm">
              <Pill tone="neutral">{t('customers.import.rows', { count: sum.rows })}</Pill>
              <Pill tone="good">{t('customers.import.newCompanies', { count: sum.newCompanies })}</Pill>
              <Pill tone="good">{t('customers.import.newContacts', { count: sum.newContacts })}</Pill>
              {sum.skipped > 0 && <Pill tone="neutral">{t('customers.import.skipped', { count: sum.skipped })}</Pill>}
              {sum.errors > 0 && <Pill tone="bad">{t('customers.import.errors', { count: sum.errors })}</Pill>}
            </div>
            {done && <Notice tone="good">{t('customers.import.done', { contacts: done.summary.newContacts, companies: done.summary.newCompanies })}</Notice>}
            <div className="scroll-box max-h-72 overflow-auto rounded-md border border-line">
              <table className="w-full text-sm">
                <thead className="sticky top-0 border-b border-line bg-sheet-2">
                  <tr>
                    <th className={th}>{t('customers.import.line')}</th>
                    <th className={th}>{t('customers.cols.company')}</th>
                    <th className={th}>{t('customers.cols.contact')}</th>
                    <th className={th}>{t('customers.import.result')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview ?? done)!.rows.map((r) => (
                    <tr key={r.line} className="border-b border-line/60 last:border-0">
                      <td className={`${td} num text-graphite`}>{r.line}</td>
                      <td className={td}>{r.company}</td>
                      <td className={td}>{r.name}</td>
                      <td className={td}>
                        <Pill tone={ACTION_TONE[r.action]}>{r.error ? t(`customers.import.errorCodes.${r.error}`) : t(`customers.import.actions.${r.action}`)}</Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Export                                                               */
/* ------------------------------------------------------------------ */

export function useContactsExport(search: string, status?: CompanyStatus) {
  const toast = useToast();
  const run = useM('customers.exportContacts', {});
  return {
    pending: run.isPending,
    start: () => run.mutate({ lang: lang(), search: search || undefined, status }, { onSuccess: (f) => saveFile(f), onError: (e) => toast.error(e) }),
  };
}

/* ------------------------------------------------------------------ */
/* Role of a company in a project                                       */
/* ------------------------------------------------------------------ */

export function PartyDialog({ companyId, onClose }: { companyId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const projects = useQ('customers.projectChoices', {});
  const detail = useQ('customers.company', { id: companyId });
  const [projectId, setProjectId] = useState('');
  const [role, setRole] = useState<PartyRole>('auftraggeber');
  const [contactId, setContactId] = useState('');
  const save = useM('customers.setPartyRole', { invalidate: ['customers.'] });
  return (
    <Modal
      open
      onClose={onClose}
      title={t('customers.party.title')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!projectId}
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { projectId, companyId, role, contactId: contactId || null },
                {
                  onSuccess: () => {
                    toast.ok(t('common.saved'));
                    onClose();
                  },
                },
              )
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t('common.project')}>
          <Select
            value={projectId}
            placeholder={t('customers.party.pickProject')}
            options={(projects.data?.projects ?? []).map((p) => ({ value: p.id, label: `${p.number} ${p.name}` }))}
            onChange={(e) => setProjectId(e.target.value)}
          />
        </Field>
        <Field label={t('customers.cols.role')}>
          <Select value={role} onChange={(e) => setRole(e.target.value as PartyRole)}>
            {PARTY_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`customers.roles.${r}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('customers.cols.contact')} hint={t('customers.party.contactHint')}>
          <Select value={contactId} onChange={(e) => setContactId(e.target.value)}>
            <option value="">{t('customers.party.noContact')}</option>
            {(detail.data?.contacts ?? [])
              .filter((c) => c.active)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {[c.firstName, c.lastName].filter(Boolean).join(' ')}
                </option>
              ))}
          </Select>
        </Field>
        <ErrorBox error={save.error} />
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Payment terms of a project                                           */
/* ------------------------------------------------------------------ */

interface TermsForm {
  recipientCompanyId: string;
  recipientAddress: string;
  orderRef: string;
  buyerRef: string;
  paymentDays: string;
  discountPercent: string;
  discountDays: string;
  adjustments: { label: string; mode: 'percent' | 'amount'; value: string }[];
  billingMethod: BillingMethod | '';
  taxScenario: TaxScenario | '';
  deliveryChannel: DeliveryChannel | '';
  deliveryAddress: string;
  snApprovalRequired: boolean;
  snTemplate: string;
  note: string;
}

const EMPTY_TERMS: TermsForm = {
  recipientCompanyId: '',
  recipientAddress: '',
  orderRef: '',
  buyerRef: '',
  paymentDays: '',
  discountPercent: '',
  discountDays: '',
  adjustments: [],
  billingMethod: '',
  taxScenario: '',
  deliveryChannel: '',
  deliveryAddress: '',
  snApprovalRequired: false,
  snTemplate: '',
  note: '',
};

type Contract = ProcOutput<'customers.contract'>;

function termsFromContract(c: Contract): TermsForm {
  const k = c.contract;
  if (!k) return { ...EMPTY_TERMS, paymentDays: c.defaultPaymentDays ? String(c.defaultPaymentDays) : '' };
  return {
    recipientCompanyId: k.recipientCompanyId ?? '',
    recipientAddress: k.recipientAddress ?? '',
    orderRef: k.orderRef ?? '',
    buyerRef: k.buyerRef ?? '',
    paymentDays: k.paymentDays === null ? '' : String(k.paymentDays),
    discountPercent: k.discountPercent === null ? '' : String(k.discountPercent),
    discountDays: k.discountDays === null ? '' : String(k.discountDays),
    adjustments: k.adjustments.map((a) => ({ label: a.label, mode: a.mode, value: a.mode === 'amount' ? eur(a.value) : String(a.value) })),
    billingMethod: (k.billingMethod as BillingMethod) ?? '',
    taxScenario: (k.taxScenario as TaxScenario) ?? '',
    deliveryChannel: (k.deliveryChannel as DeliveryChannel) ?? '',
    deliveryAddress: k.deliveryAddress ?? '',
    snApprovalRequired: k.snApprovalRequired,
    snTemplate: k.snTemplate ?? '',
    note: k.note ?? '',
  };
}

function adjustmentsOf(f: TermsForm): PaymentAdjustment[] {
  return f.adjustments
    .filter((a) => a.label.trim() && (num(a.value) ?? 0) > 0)
    .map((a) => ({ label: a.label.trim(), mode: a.mode, value: a.mode === 'amount' ? cents(a.value) : (num(a.value) ?? 0) }));
}

/**
 * Commercial terms of one project: who gets the invoice, payment term (days, discount for early payment, deductions
 * after VAT), way of billing, tax case and delivery. The example calculation shows what the customer pays; the invoice
 * itself is not created here.
 */
export function TermsDialog({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const q = useQ('customers.contract', { projectId });
  const companies = useQ('customers.companies', {});
  const save = useM('customers.saveContract', { invalidate: ['customers.'] });
  const [f, setF] = useState<TermsForm>(EMPTY_TERMS);
  const [loaded, setLoaded] = useState<string | null>(null);
  const [example, setExample] = useState('10000');
  const [itemEdit, setItemEdit] = useState<ItemForm | null>(null);
  useEffect(() => {
    if (q.data && loaded !== projectId) {
      setF(termsFromContract(q.data));
      setLoaded(projectId);
    }
  }, [q.data, loaded, projectId]);
  const set = <K extends keyof TermsForm>(k: K, v: TermsForm[K]) => setF((x) => ({ ...x, [k]: v }));
  const adjustments = adjustmentsOf(f);
  const days = num(f.paymentDays);
  const pctNum = num(f.discountPercent);
  const discDays = num(f.discountDays);

  // example calculation: asks the server (same rules as the later invoice draft)
  const preview = useQ(
    'customers.previewPayment',
    { netCents: cents(example), paymentDays: days === null ? null : Math.round(days), discountPercent: pctNum, discountDays: discDays === null ? null : Math.round(discDays), adjustments, lang: lang() === 'en' ? 'en' : 'de' },
    { enabled: !!q.data },
  );

  const data = q.data;
  const gapsNow = useMemo(() => {
    // live check of the form: the codes the server reports after saving, plus the ones visible right away
    const g: string[] = [];
    if (!f.recipientCompanyId) g.push('recipient');
    if (days === null) g.push('payment_days');
    if (pctNum && (!discDays || (days !== null && discDays >= days) || pctNum > MAX_DISCOUNT_PERCENT)) g.push('discount');
    if (!f.billingMethod) g.push('billing_method');
    if (!f.taxScenario) g.push('tax_scenario');
    if (!f.deliveryChannel || (f.deliveryChannel !== 'portal' && !f.deliveryAddress.trim())) g.push('delivery');
    return g;
  }, [f, days, pctNum, discDays]);

  const submit = () =>
    save.mutate(
      {
        projectId,
        recipientCompanyId: f.recipientCompanyId || null,
        recipientAddress: f.recipientAddress.trim() || null,
        orderRef: f.orderRef.trim() || null,
        buyerRef: f.buyerRef.trim() || null,
        paymentDays: days === null ? null : Math.round(days),
        discountPercent: pctNum,
        discountDays: discDays === null ? null : Math.round(discDays),
        adjustments,
        billingMethod: f.billingMethod || null,
        taxScenario: f.taxScenario || null,
        deliveryChannel: f.deliveryChannel || null,
        deliveryAddress: f.deliveryAddress.trim() || null,
        snApprovalRequired: f.snApprovalRequired,
        snTemplate: f.snTemplate.trim() || null,
        note: f.note.trim() || null,
      },
      {
        onSuccess: () => {
          toast.ok(t('common.saved'));
          void q.refetch();
        },
      },
    );

  return (
    <>
      <Modal
        open
        wide
        onClose={onClose}
        title={data ? `${t('customers.terms.title')} · ${data.project.number} ${data.project.name}` : t('customers.terms.title')}
        footer={
          <>
            <Button onClick={onClose}>{t('common.close')}</Button>
            <Button variant="primary" loading={save.isPending} disabled={!data} onClick={submit}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        {q.isLoading || !data ? (
          <Loading />
        ) : (
          <div className="flex flex-col gap-5">
            <ErrorBox error={q.error ?? save.error} />
            {(data.project.customerName || data.project.partner) && (
              <p className="text-xs text-graphite">
                {t('customers.terms.freeText')}: {[data.project.customerName && `${t('customers.terms.customer')}: ${data.project.customerName}`, data.project.partner && `${t('customers.terms.partner')}: ${data.project.partner}`].filter(Boolean).join(' · ')}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-1.5">
              {gapsNow.length === 0 ? (
                <Pill tone="good">{t('customers.terms.complete')}</Pill>
              ) : (
                <>
                  <span className="text-xs text-graphite">{t('customers.terms.stillNeeded')}:</span>
                  {gapsNow.map((g) => (
                    <Pill key={g} tone="warn">
                      {t(`customers.gaps.${g as 'recipient'}`)}
                    </Pill>
                  ))}
                </>
              )}
              {data.contract && data.gaps.filter((g) => !gapsNow.includes(g)).map((g) => <Pill key={g} tone="warn">{t(`customers.gaps.${g}`)}</Pill>)}
            </div>

            <Section title={t('customers.terms.sectionRecipient')}>
              <Field label={t('customers.terms.recipient')}>
                <Select
                  value={f.recipientCompanyId}
                  placeholder={t('customers.terms.noRecipient')}
                  options={(companies.data?.companies ?? []).map((c) => ({ value: c.id, label: c.city ? `${c.name} (${c.city})` : c.name }))}
                  onChange={(e) => set('recipientCompanyId', e.target.value)}
                />
              </Field>
              <Field label={t('customers.terms.recipientAddress')} hint={t('customers.terms.recipientAddressHint')}>
                <Textarea rows={2} value={f.recipientAddress} onChange={(e) => set('recipientAddress', e.target.value)} />
              </Field>
              <Field label={t('customers.terms.orderRef')}>
                <Input value={f.orderRef} onChange={(e) => set('orderRef', e.target.value)} />
              </Field>
              <Field label={t('customers.terms.buyerRef')}>
                <Input value={f.buyerRef} onChange={(e) => set('buyerRef', e.target.value)} />
              </Field>
            </Section>

            <Section title={t('customers.terms.sectionPayment')} hint={t('customers.terms.paymentIntro')}>
              <PaymentFields value={f} onChange={(patch) => setF((x) => ({ ...x, ...patch }))} daysHint={t('customers.terms.paymentHint', { days: data.defaultPaymentDays })} />
              <div className="rounded-md border border-line bg-sheet-2 p-3 md:col-span-2">
                <div className="mb-2 flex flex-wrap items-end justify-between gap-3">
                  <span className="text-sm font-semibold">{t('customers.terms.exampleTitle')}</span>
                  <Field label={t('customers.terms.exampleNet')} className="w-40">
                    <Input inputMode="decimal" value={example} onChange={(e) => setExample(e.target.value)} />
                  </Field>
                </div>
                {preview.data && <PaymentBreakdownTable breakdown={preview.data.breakdown} vatPercent={data.vatPercent} days={days === null ? null : Math.round(days)} text={preview.data.text} />}
              </div>
            </Section>

            <Section title={t('customers.terms.sectionBilling')}>
              <Field label={t('customers.terms.method')}>
                <Select value={f.billingMethod} onChange={(e) => set('billingMethod', e.target.value as BillingMethod)}>
                  <option value="">{t('customers.terms.notChosen')}</option>
                  {BILLING_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {t(`customers.methods.${m}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('customers.terms.taxScenario')} hint={f.taxScenario === 'pending_advisor' ? t('customers.terms.taxOpen') : undefined}>
                <Select value={f.taxScenario} onChange={(e) => set('taxScenario', e.target.value as TaxScenario)}>
                  <option value="">{t('customers.terms.notChosen')}</option>
                  {TAX_SCENARIOS.map((s) => (
                    <option key={s} value={s}>
                      {t(`customers.taxScenarios.${s}`)}
                    </option>
                  ))}
                </Select>
              </Field>
            </Section>

            <Section title={t('customers.terms.sectionDelivery')}>
              <Field label={t('customers.terms.delivery')}>
                <Select value={f.deliveryChannel} onChange={(e) => set('deliveryChannel', e.target.value as DeliveryChannel)}>
                  <option value="">{t('customers.terms.notChosen')}</option>
                  {DELIVERY_CHANNELS.map((c) => (
                    <option key={c} value={c}>
                      {t(`customers.channels.${c}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('customers.terms.deliveryAddress')}>
                <Input value={f.deliveryAddress} disabled={f.deliveryChannel === 'portal'} onChange={(e) => set('deliveryAddress', e.target.value)} />
              </Field>
              <Checkbox className="md:col-span-2" label={t('customers.terms.snApproval')} checked={f.snApprovalRequired} onChange={(e) => set('snApprovalRequired', e.target.checked)} />
              <Field label={t('customers.terms.snTemplate')}>
                <Input value={f.snTemplate} onChange={(e) => set('snTemplate', e.target.value)} />
              </Field>
              <Field label={t('customers.terms.note')}>
                <Input value={f.note} onChange={(e) => set('note', e.target.value)} />
              </Field>
            </Section>

            <section>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold">{t('customers.terms.itemsTitle')}</h3>
                <Button size="sm" icon={<Plus className="size-3.5" />} disabled={!data.contract} onClick={() => setItemEdit({ projectId, kind: 'nachtrag', title: '', amount: '', date: '', status: 'offered', note: '' })}>
                  {t('customers.terms.addItem')}
                </Button>
              </div>
              {!data.contract && <p className="text-xs text-graphite">{t('customers.terms.needTerms')}</p>}
              {data.contract && data.items.length === 0 && <p className="text-xs text-graphite">{t('customers.terms.noItems')}</p>}
              {data.items.length > 0 && (
                <>
                  <ul className="divide-y divide-line/70 rounded-md border border-line">
                    {data.items.map((it) => (
                      <li key={it.id}>
                        <button
                          type="button"
                          className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-left text-sm hover:bg-sheet-2"
                          onClick={() => setItemEdit({ id: it.id, projectId, kind: it.kind as ItemKind, title: it.title, amount: eur(it.amountCents), date: it.date ?? '', status: it.status, note: it.note ?? '' })}
                        >
                          <Pill tone="neutral">{t(`customers.itemKinds.${it.kind as 'nachtrag'}`)}</Pill>
                          <span className="min-w-0 flex-1 truncate">{it.title}</span>
                          <Pill tone={it.status === 'approved' || it.status === 'invoiced' ? 'good' : it.status === 'rejected' ? 'bad' : 'warn'}>{t(`customers.itemStatuses.${it.status}`)}</Pill>
                          <span className="num w-28 text-right">{money(it.amountCents)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <p className="num mt-1 text-right text-sm">
                    {t('customers.terms.itemsTotal')}: <b>{money(data.itemsTotalCents)}</b>
                  </p>
                </>
              )}
            </section>
          </div>
        )}
      </Modal>
      {itemEdit && <ItemDialog form={itemEdit} onClose={() => setItemEdit(null)} onSaved={() => void q.refetch()} />}
    </>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-sm font-semibold">{title}</h3>
      {hint && <p className="mt-0.5 mb-2 text-xs text-graphite">{hint}</p>}
      <div className="mt-2 grid gap-3 md:grid-cols-2">{children}</div>
    </section>
  );
}

interface ItemForm {
  id?: string;
  projectId: string;
  kind: ItemKind;
  title: string;
  amount: string;
  date: string;
  status: ItemStatus;
  note: string;
}

function ItemDialog({ form, onClose, onSaved }: { form: ItemForm; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [f, setF] = useState(form);
  const save = useM('customers.saveItem', { invalidate: ['customers.'] });
  const del = useM('customers.deleteItem', { invalidate: ['customers.'] });
  const set = <K extends keyof ItemForm>(k: K, v: ItemForm[K]) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Modal
      open
      onClose={onClose}
      title={f.id ? t('customers.terms.itemEdit') : t('customers.terms.addItem')}
      footer={
        <>
          {f.id && (
            <Button
              variant="danger"
              className="mr-auto"
              loading={del.isPending}
              onClick={() =>
                del.mutate(
                  { id: f.id! },
                  {
                    onSuccess: () => {
                      onSaved();
                      onClose();
                    },
                  },
                )
              }
            >
              {t('common.delete')}
            </Button>
          )}
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!f.title.trim() || num(f.amount) === null}
            onClick={() =>
              save.mutate(
                { id: f.id, projectId: f.projectId, kind: f.kind, title: f.title.trim(), amountCents: cents(f.amount), date: f.date || null, status: f.status, note: f.note.trim() || null },
                {
                  onSuccess: () => {
                    toast.ok(t('common.saved'));
                    onSaved();
                    onClose();
                  },
                },
              )
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 md:grid-cols-2">
        <Field label={t('customers.cols.kind')}>
          <Select value={f.kind} onChange={(e) => set('kind', e.target.value as ItemKind)}>
            {ITEM_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`customers.itemKinds.${k}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('common.status')}>
          <Select value={f.status} onChange={(e) => set('status', e.target.value as ItemStatus)}>
            {ITEM_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`customers.itemStatuses.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('customers.terms.itemTitle')} className="md:col-span-2">
          <Input value={f.title} maxLength={200} onChange={(e) => set('title', e.target.value)} />
        </Field>
        <Field label={t('customers.terms.itemAmount')}>
          <Input inputMode="decimal" value={f.amount} onChange={(e) => set('amount', e.target.value)} />
        </Field>
        <Field label={t('common.date')}>
          <Input type="date" value={f.date} onChange={(e) => set('date', e.target.value)} />
        </Field>
        <Field label={t('customers.terms.note')} className="md:col-span-2">
          <Input value={f.note} onChange={(e) => set('note', e.target.value)} />
        </Field>
      </div>
      <div className="mt-3">
        <ErrorBox error={save.error ?? del.error} />
      </div>
    </Modal>
  );
}

/** "14 Tage" or "14 Tage, 1,5 % Skonto in 7 Tagen": the payment term in one line for lists. */
export function usePaymentLine() {
  const { t } = useTranslation();
  return (days: number | null, percent: number | null, discountDays: number | null) => {
    if (days === null) return '–';
    const base = t('customers.terms.daysShort', { count: days });
    return percent && discountDays ? `${base}, ${t('customers.terms.discountShort', { percent: String(percent).replace('.', lang() === 'en' ? '.' : ','), days: discountDays })}` : base;
  };
}
